import type { Policy, WorkflowType } from '@communities/content-management/domain';
import { Dialog, type DialogAction, StepperDots, type StepConfig } from '@soteria/design-system-ve';
import { useStandardLabels } from '@system-config/label-management/hooks';

import { PolicyDialogMode } from '../PolicyDialog';

import { dialogHeaderStyles, dialogTitleStyles, dialogContentStyles, dialogStepContentStyles } from './CreatePolicyDialog.css';
import { DialogProvider, useDialogContext, validateStepTransition } from './components/DialogProvider';
import { PolicyInfoStep, TextLabelsStep, ImageLabelsStep, ReviewStep } from './components/Steps';
import { DialogStep } from './types';

/**
 * CreatePolicyDialog component props
 */
export interface CreatePolicyDialogProps {
  isOpen: boolean;
  onClose: () => void;
  communityId: string;
  workflowType: WorkflowType;
  policy?: Policy;
  mode?: PolicyDialogMode;
}

/**
 * Dialog step content component
 */
function DialogStepContent() {
  const { state } = useDialogContext();
  const { labels: standardLabels, loading: labelsLoading } = useStandardLabels();

  switch (state.currentStep) {
    case DialogStep.POLICY_INFO:
      return <PolicyInfoStep />;
    case DialogStep.TEXT_LABELS:
      return <TextLabelsStep labelsLoading={labelsLoading} standardLabels={standardLabels} />;
    case DialogStep.IMAGE_LABELS:
      return <ImageLabelsStep labelsLoading={labelsLoading} standardLabels={standardLabels} />;
    case DialogStep.REVIEW:
      return <ReviewStep />;
    default:
      return null;
  }
}

/**
 * Custom dialog header component
 */
function DialogHeader({ title }: { title: string }) {
  const { state, actions, isEditMode } = useDialogContext();

  // Tooltip message for steps that require policy name
  const policyNameRequiredTooltip = 'Please enter a policy name to continue';

  // Convert dialog steps to StepperDots format
  const stepperDotsSteps: StepConfig[] = [
    {
      id: 'policy-info',
      label: 'Basic Information',
      description: 'Policy name and settings',
    },
    {
      id: 'text-labels',
      label: 'Text Content',
      description: 'Configure text label rules',
      disabledTooltip: policyNameRequiredTooltip,
    },
    {
      id: 'image-labels',
      label: 'Image Content',
      description: 'Configure image label rules',
      disabledTooltip: policyNameRequiredTooltip,
    },
    {
      id: 'review',
      label: isEditMode ? 'Save' : 'Review & Create',
      description: isEditMode ? 'Save policy changes' : 'Confirm policy settings',
      disabledTooltip: policyNameRequiredTooltip,
    },
  ];

  const stepOrder = [DialogStep.POLICY_INFO, DialogStep.TEXT_LABELS, DialogStep.IMAGE_LABELS, DialogStep.REVIEW];
  const currentStepIndex = stepOrder.indexOf(state.currentStep);

  const handleStepperClick = (index: number) => {
    const targetStep = stepOrder[index];

    actions.goToStep(targetStep);
  };

  const canNavigateToStepIndex = (fromIndex: number, toIndex: number): boolean => {
    const fromStep = stepOrder[fromIndex];
    const toStep = stepOrder[toIndex];

    return validateStepTransition(fromStep, toStep, state.formData);
  };

  return (
    <div className={dialogHeaderStyles}>
      <h2 className={dialogTitleStyles}>{title}</h2>

      <StepperDots
        interactive
        showDescriptions
        showLabels
        activeStep={currentStepIndex}
        canNavigateToStep={canNavigateToStepIndex}
        steps={stepperDotsSteps}
        variant="numbers"
        onStepClick={handleStepperClick}
      />
    </div>
  );
}

/**
 * Get step information for navigation
 */
function getStepInfo(step: DialogStep): { isFirst: boolean; isLast: boolean } {
  return {
    isFirst: step === DialogStep.POLICY_INFO,
    isLast: step === DialogStep.REVIEW,
  };
}

/**
 * Get button labels based on current step and edit mode
 */
function getButtonLabels(step: DialogStep, isEditMode: boolean, isDuplicateMode: boolean): { next: string; back: string } {
  switch (step) {
    case DialogStep.POLICY_INFO:
      return { next: 'Next: Text Labels', back: 'Back' };
    case DialogStep.TEXT_LABELS:
      return { next: 'Next: Image Labels', back: 'Back: Policy Info' };
    case DialogStep.IMAGE_LABELS:
      return { next: 'Next: Review', back: 'Back: Text Labels' };
    case DialogStep.REVIEW:
      return { next: isEditMode ? 'Save Policy' : isDuplicateMode ? 'Create Copy' : 'Create Policy', back: 'Back: Image Labels' };
    default:
      return { next: 'Next', back: 'Back' };
  }
}

/**
 * Dialog content wrapper
 */
function DialogContent({ onClose, title }: { onClose: () => void; title: string }) {
  const { state, actions, isEditMode, isDuplicateMode } = useDialogContext();

  const stepInfo = getStepInfo(state.currentStep);
  const buttonLabels = getButtonLabels(state.currentStep, isEditMode, isDuplicateMode);

  const handleNext = () => {
    if (stepInfo.isLast) {
      actions.submitDialog();
    } else {
      actions.goToNextStep();
    }
  };

  const handleBack = () => {
    if (stepInfo.isFirst) {
      onClose();
    } else {
      actions.goToPreviousStep();
    }
  };

  const isNextDisabled = state.isSubmitting || (stepInfo.isLast && (!state.isValid || state.errors.length > 0));

  // Define dialog actions for the fixed footer
  // Note: Dialog renders primaryAction on the left and secondaryAction on the right
  // So we assign Cancel/Back to primaryAction (left) and Next/Create to secondaryAction (right)
  const primaryAction: DialogAction = {
    label: stepInfo.isFirst ? 'Cancel' : buttonLabels.back,
    onClick: handleBack,
    disabled: state.isSubmitting,
    variant: 'secondary',
  };

  const secondaryAction: DialogAction = {
    label: state.isSubmitting && stepInfo.isLast ? (isEditMode ? 'Saving...' : 'Creating...') : buttonLabels.next,
    onClick: handleNext,
    disabled: isNextDisabled,
    variant: 'primary',
  };

  return (
    <Dialog isOpen customHeader={<DialogHeader title={title} />} primaryAction={primaryAction} secondaryAction={secondaryAction} size="xxl" onClose={onClose}>
      <div className={dialogContentStyles}>
        <div className={dialogStepContentStyles}>
          <DialogStepContent />
        </div>
      </div>
    </Dialog>
  );
}

/**
 * CreatePolicyDialog component
 */
export function CreatePolicyDialog({ isOpen, onClose, communityId, workflowType, policy, mode = PolicyDialogMode.Create }: CreatePolicyDialogProps) {
  if (!isOpen) {
    return null;
  }

  const isEditMode = mode === PolicyDialogMode.Edit;
  const isDuplicateMode = mode === PolicyDialogMode.Duplicate;
  const dialogTitle = isEditMode ? 'Edit Policy' : isDuplicateMode ? 'Duplicate Policy' : 'Create Policy';

  return (
    <DialogProvider communityId={communityId} initialPolicy={policy} mode={mode} workflowType={workflowType} onCancel={onClose} onComplete={onClose}>
      <DialogContent title={dialogTitle} onClose={onClose} />
    </DialogProvider>
  );
}
